/**
 * Recovery-phrase display + acknowledgement gate.
 *
 * Read the 32-byte family key out of the in-memory signal populated by
 * signup, render the 24 BIP-39 words numbered 1..24, and force the user
 * to (a) tick the "I've written it down" checkbox AND (b) re-type three
 * randomly-chosen words from the phrase before we let them proceed.
 *
 * This is the ONLY chance to save the phrase. Without it, a lost password
 * means permanent data loss — so the treatment is deliberately deliberate:
 * poppy-numbered mono word grid, no dashboard chrome, one call to action.
 */

import { useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { mnemonicFromFamilyKey } from '../e2ee/mnemonic';
import { familyKeyInMemory } from '../state/session';
import { checkoutIntent } from '../state/checkout-intent';

const CHALLENGE_COUNT = 3;
const WORD_COUNT = 24;

function pickChallengeIndexes(n: number, max: number): number[] {
  const picked = new Set<number>();
  const bytes = new Uint8Array(n * 4);
  crypto.getRandomValues(bytes);
  let cursor = 0;
  while (picked.size < n) {
    if (cursor >= bytes.length) {
      crypto.getRandomValues(bytes);
      cursor = 0;
    }
    const b = bytes[cursor] ?? 0;
    cursor += 1;
    const idx = (b % max) + 1;
    picked.add(idx);
  }
  return Array.from(picked).sort((a, b) => a - b);
}

export function MnemonicShowRoute() {
  const location = useLocation();
  const fk = familyKeyInMemory.value;

  const words = useMemo<string[] | null>(() => {
    if (fk === null) return null;
    return mnemonicFromFamilyKey(fk).split(' ');
  }, [fk]);

  const challengeIndexes = useMemo(() => {
    if (words === null) return [] as number[];
    return pickChallengeIndexes(CHALLENGE_COUNT, WORD_COUNT);
  }, [words]);

  const [ack, setAck] = useState(false);
  const [answers, setAnswers] = useState<string[]>(() =>
    Array<string>(CHALLENGE_COUNT).fill(''),
  );
  const [copied, setCopied] = useState(false);

  async function onCopy(): Promise<void> {
    if (words === null) return;
    await navigator.clipboard.writeText(words.join(' '));
    setCopied(true);
    window.setTimeout(() => {
      setCopied(false);
    }, 2000);
  }

  if (words === null) {
    return (
      <main class="mx-auto max-w-xl px-6 py-24">
        <div class="eyebrow mb-6">Recovery</div>
        <h1 class="display-italic text-[36px] leading-tight mb-4">
          No recovery phrase available.
        </h1>
        <p class="text-surface-body">
          Your family key isn&apos;t loaded. Sign in again to see the phrase, or
          start over from signup.
        </p>
      </main>
    );
  }

  const allCorrect = challengeIndexes.every((idx, i) => {
    const expected = words[idx - 1];
    const given = answers[i];
    if (expected === undefined || given === undefined) return false;
    return given.trim().toLowerCase() === expected.toLowerCase();
  });
  const canContinue = ack && allCorrect;

  function updateAnswer(i: number, v: string): void {
    setAnswers((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
  }

  function onContinue(): void {
    // Plan 030: if signup arrived from a paid pricing CTA, go straight to
    // the checkout intercept so the parent can enter a card while the
    // decision is fresh. Otherwise dashboard as before.
    if (checkoutIntent.value?.paid === true) {
      location.route('/checkout/redirect');
      return;
    }
    location.route('/dashboard');
  }

  return (
    <main class="mx-auto max-w-3xl px-6 py-16">
      <div class="eyebrow mb-6">Recovery</div>
      <h1 class="display-italic text-[44px] leading-tight mb-4 max-w-[20ch]">
        Save your recovery phrase.
      </h1>
      <p class="text-surface-body max-w-[62ch] mb-10">
        These 24 words are the only way to recover your family&apos;s data if
        you lose your password. Write them down and store them somewhere safe.
        If you lose them AND your password, we cannot help you.
      </p>

      {/* The words themselves — the moment of gravity. */}
      <ol class="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3 mb-6 list-none p-0">
        {words.map((w, i) => (
          <li class="flex items-baseline gap-3" key={i.toString()}>
            <span class="font-mono text-[12px] text-signal tracking-[0.06em] tabular-nums w-6">
              {(i + 1).toString().padStart(2, '0')}
            </span>
            <span class="text-[19px] text-surface-fg leading-none">{w}</span>
          </li>
        ))}
      </ol>

      <div class="mb-12">
        <button
          type="button"
          onClick={() => {
            void onCopy();
          }}
          class="font-mono text-[11px] tracking-[0.14em] uppercase text-surface-muted hover:text-signal border border-line hover:border-signal px-3 py-2 transition-colors"
        >
          {copied ? 'Copied' : 'Copy all 24 words'}
        </button>
      </div>

      {/* Verification challenge — enforces attention */}
      <div class="border-t border-line pt-8 mb-8">
        <div class="eyebrow mb-4">Verify</div>
        <p class="text-surface-body mb-5">
          Type these three words from the phrase above.
        </p>
        <div class="grid gap-4 md:grid-cols-3">
          {challengeIndexes.map((idx, i) => (
            <label class="block space-y-2" key={idx.toString()}>
              <span class="font-mono text-[11px] text-surface-muted tracking-[0.08em] uppercase">
                Word #{idx.toString().padStart(2, '0')}
              </span>
              <Input
                type="text"
                value={answers[i] ?? ''}
                onInput={(e: Event) => {
                  updateAnswer(i, (e.target as HTMLInputElement).value);
                }}
                autocomplete="off"
                autocapitalize="none"
                spellcheck={false}
              />
            </label>
          ))}
        </div>
      </div>

      <label class="flex items-start gap-3 mb-8 text-[15px] text-surface-fg">
        <input
          type="checkbox"
          class="mt-1 accent-[color:var(--color-signal)]"
          checked={ack}
          onChange={(e: Event) => {
            setAck((e.target as HTMLInputElement).checked);
          }}
        />
        <span>
          I&apos;ve written these down. I understand losing them means losing my
          data.
        </span>
      </label>

      <Button disabled={!canContinue} onClick={onContinue}>
        {checkoutIntent.value?.paid === true
          ? 'Continue to checkout'
          : 'Continue to dashboard'}
      </Button>
    </main>
  );
}
